// src/components/HeaderEditor.js
import ColorInput from './ColorInput';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    COMPONENT_LIST_ALIGNMENTS,
    COMPONENT_LIST_PLACEMENTS,
    COMPONENT_LIST_WIDTHS,
    COMPONENT_TYPE_LIST_OPTIONS,
    normalizeComponentListSettings,
} from '../utils/ComponentTypeLists';
import './MuultiStyle.css';
import './HeaderEditor.css';

export default function HeaderEditor({
                                         // content
                                         profilePicUrl,
                                         pageTitle,
                                         pageDescription,
                                         alignment,

                                         // header background + border (LIVE state from parent)
                                         headerBackground,
                                         onHeaderBackgroundChange,

                                         // header padding (LIVE state from parent)
                                         headerPadding,
                                         onHeaderPaddingChange,

                                         // basic callbacks
                                         onFileChange,
                                         onTitleChange,
                                         onDescriptionChange,
                                         onAlignmentChange,

                                         componentListSettings,
                                         componentListCounts,
                                         onComponentListSettingsChange,
                                         showFollowerCount = false,
                                         onShowFollowerCountChange,

                                         // save (persists everything)
                                         onSave
                                     }) {
    // preview for profile pic selection
    const [previewUrl, setPreviewUrl] = useState(profilePicUrl || '');
    useEffect(() => {
        setPreviewUrl(profilePicUrl || '');
    }, [profilePicUrl]);

    // safe defaults (keep your schema)
    const hb = headerBackground || {
        type: 'color',
        color: '#ffffff',
        colors: ['#ffffff', '#000000'],
        direction: 'to right',
        imageUrl: '',
        imageFile: null, // ✅ file lives here until Save uploads it
        sizing: 'cover',
        position: 'center',
        fontColor: '#000000',
        borderStyle: 'none',
        borderWidth: 0,
        borderColor: 'transparent',
        borderRadius: 0
    };

    const hp = headerPadding || {
        mode: 'per-side',
        unit: 'px',
        all: 24,
        top: 24,
        right: 24,
        bottom: 24,
        left: 24
    };

    const updateHB = (patch) => onHeaderBackgroundChange?.({ ...hb, ...patch });
    const updateHP = (patch) => onHeaderPaddingChange?.({ ...hp, ...patch });
    const normalizedComponentListSettings = useMemo(
        () => normalizeComponentListSettings(componentListSettings),
        [componentListSettings]
    );
    const listCounts = componentListCounts || {};
    const updateComponentListSetting = (type, enabled) => {
        onComponentListSettingsChange?.({
            ...normalizedComponentListSettings,
            [type]: !!enabled,
        });
    };
    const updateComponentListSettings = (patch) => {
        onComponentListSettingsChange?.({
            ...normalizedComponentListSettings,
            ...patch,
        });
    };
    const updateComponentListButtonStyle = (patch) => {
        onComponentListSettingsChange?.({
            ...normalizedComponentListSettings,
            buttonStyle: {
                ...(normalizedComponentListSettings.buttonStyle || {}),
                ...patch,
            },
        });
    };
    const componentListButtonStyle = normalizedComponentListSettings.buttonStyle || {};

    // local convenience for gradient colors
    const gradColors = useMemo(
        () =>
            Array.isArray(hb.colors) && hb.colors.length >= 2
                ? hb.colors
                : ['#ffffff', '#000000'],
        [hb.colors]
    );

    // Keep track of blob URL we created so we can revoke it (avoid memory leaks)
    const lastBlobUrlRef = useRef(null);

    const revokeLastBlob = () => {
        const u = lastBlobUrlRef.current;
        if (u && typeof u === 'string' && u.startsWith('blob:')) {
            try { URL.revokeObjectURL(u); } catch {}
        }
        lastBlobUrlRef.current = null;
    };

    useEffect(() => {
        // Cleanup on unmount
        return () => revokeLastBlob();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ NO UPLOAD HERE. Just store file + blob preview.
    const handleHeaderBgImageFile = (file) => {
        if (!file) return;

        revokeLastBlob();

        const blobUrl = URL.createObjectURL(file);
        lastBlobUrlRef.current = blobUrl;

        updateHB({
            type: 'image',
            imageUrl: blobUrl,   // preview now
            imageFile: file,     // MuultiPage.handleSaveHeader will upload on Save
        });
    };

    return (
        <div className="header-editor header-editor-root">
            <h3 className="header-editor-title">Header Settings</h3>

            {/* top row: profile image + title/description/alignment */}
            <div className="header-preview">
                {/* Left: profile image + description */}
                <div className="header-preview-column">
                    <label>
                        Feature Image:
                        {previewUrl && (
                            <img
                                src={previewUrl}
                                className="profile-pic-preview"
                                alt="Profile preview"
                            />
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => onFileChange?.(e.target.files?.[0] || null)}
                            className="header-file-input"
                        />
                    </label>

                    <label>
                        Site Description:
                        <textarea
                            placeholder="Short description"
                            value={pageDescription || ''}
                            onChange={(e) => onDescriptionChange?.(e.target.value)}
                            rows={4}
                        />
                    </label>
                </div>

                {/* Right: title + align */}
                <div className="header-preview-column">
                    <label>
                        Site Title:
                        <input
                            type="text"
                            placeholder="Your page title"
                            value={pageTitle || ''}
                            onChange={(e) => onTitleChange?.(e.target.value)}
                        />
                    </label>

                    <label>
                        Align:
                        <select
                            value={alignment || 'left'}
                            onChange={(e) => onAlignmentChange?.(e.target.value)}
                        >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                        </select>
                    </label>
                </div>
            </div>

            {/* Header Background */}
            <div className="header-editor header-section">
                <h4 className="header-section-title">Header Background</h4>

                <div className="header-section-grid grid-3">
                    <label>
                        Type
                        <select
                            value={hb.type || 'color'}
                            onChange={(e) => updateHB({ type: e.target.value })}
                        >
                            <option value="color">Color</option>
                            <option value="gradient">Gradient</option>
                            <option value="image">Image</option>
                        </select>
                    </label>

                    <label>
                        Font Color
                        <ColorInput
                            value={hb.fontColor || '#000000'}
                            onChange={(e) => updateHB({ fontColor: e.target.value })}
                        />
                    </label>

                    <div />
                </div>

                {/* COLOR */}
                {hb.type === 'color' && (
                    <div className="header-section-grid grid-1 header-section-grid--spaced">
                        <label>
                            Background Color
                            <ColorInput
                                value={hb.color || '#ffffff'}
                                onChange={(e) => updateHB({ color: e.target.value })}
                                className="header-color-swatch"
                            />
                        </label>
                    </div>
                )}

                {/* GRADIENT */}
                {hb.type === 'gradient' && (
                    <div className="header-section-grid grid-3 header-section-grid--spaced">
                        <label>
                            From
                            <ColorInput
                                value={gradColors[0]}
                                onChange={(e) => updateHB({ colors: [e.target.value, gradColors[1]] })}
                            />
                        </label>
                        <label>
                            To
                            <ColorInput
                                value={gradColors[1]}
                                onChange={(e) => updateHB({ colors: [gradColors[0], e.target.value] })}
                            />
                        </label>
                        <label>
                            Direction
                            <select
                                value={hb.direction || 'to right'}
                                onChange={(e) => updateHB({ direction: e.target.value })}
                            >
                                <option value="to right">Left → Right</option>
                                <option value="to left">Right → Left</option>
                                <option value="to bottom">Top → Bottom</option>
                                <option value="to top">Bottom → Top</option>
                                <option value="135deg">Diagonal</option>
                            </select>
                        </label>
                    </div>
                )}

                {/* IMAGE */}
                {hb.type === 'image' && (
                    <div className="header-section-grid grid-3 header-section-grid--spaced">
                        <label>
                            Background Image
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleHeaderBgImageFile(e.target.files?.[0] || null)}
                                className="header-file-input"
                            />
                        </label>

                        <label>
                            Sizing
                            <select
                                value={hb.sizing || 'cover'}
                                onChange={(e) => updateHB({ sizing: e.target.value })}
                            >
                                <option value="cover">Cover</option>
                                <option value="contain">Contain</option>
                                <option value="auto">Auto</option>
                            </select>
                        </label>

                        <label>
                            Position
                            <select
                                value={hb.position || 'center'}
                                onChange={(e) => updateHB({ position: e.target.value })}
                            >
                                <option value="center">Center</option>
                                <option value="top">Top</option>
                                <option value="bottom">Bottom</option>
                                <option value="left">Left</option>
                                <option value="right">Right</option>
                            </select>
                        </label>
                    </div>
                )}

                {/* Borders */}
                <div className="header-section-grid grid-3 header-section-grid--spaced">
                    <label>
                        Border Style
                        <select
                            value={hb.borderStyle || 'none'}
                            onChange={(e) => updateHB({ borderStyle: e.target.value })}
                        >
                            <option value="none">None</option>
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="dotted">Dotted</option>
                        </select>
                    </label>

                    <label>
                        Border Width
                        <input
                            type="number"
                            value={hb.borderWidth ?? 0}
                            onChange={(e) => updateHB({ borderWidth: Number(e.target.value || 0) })}
                            min={0}
                        />
                    </label>

                    <label>
                        Border Color
                        <ColorInput
                            value={hb.borderColor || '#000000'}
                            onChange={(e) => updateHB({ borderColor: e.target.value })}
                        />
                    </label>

                    <label>
                        Border Radius
                        <input
                            type="number"
                            value={hb.borderRadius ?? 0}
                            onChange={(e) => updateHB({ borderRadius: Number(e.target.value || 0) })}
                            min={0}
                        />
                    </label>
                </div>
            </div>

            {/* Header Padding */}
            <div className="header-editor header-section">
                <h4 className="header-section-title">Header Padding</h4>

                <div className="header-section-grid grid-3">
                    <label>
                        Mode
                        <select
                            value={hp.mode || 'per-side'}
                            onChange={(e) => updateHP({ mode: e.target.value })}
                        >
                            <option value="per-side">Per Side</option>
                            <option value="all-sides">All Sides</option>
                        </select>
                    </label>

                    <label>
                        Unit
                        <select
                            value={hp.unit || 'px'}
                            onChange={(e) => updateHP({ unit: e.target.value })}
                        >
                            <option value="px">px</option>
                            <option value="%">%</option>
                            <option value="rem">rem</option>
                        </select>
                    </label>

                    <div />
                </div>

                {hp.mode === 'all-sides' ? (
                    <div className="header-section-grid grid-1 header-section-grid--spaced">
                        <label>
                            Padding
                            <input
                                type="number"
                                value={hp.all ?? 0}
                                onChange={(e) => updateHP({ all: Number(e.target.value || 0) })}
                                min={0}
                            />
                        </label>
                    </div>
                ) : (
                    <div className="header-section-grid grid-4 header-section-grid--spaced">
                        <label>
                            Top
                            <input
                                type="number"
                                value={hp.top ?? 0}
                                onChange={(e) => updateHP({ top: Number(e.target.value || 0) })}
                                min={0}
                            />
                        </label>
                        <label>
                            Right
                            <input
                                type="number"
                                value={hp.right ?? 0}
                                onChange={(e) => updateHP({ right: Number(e.target.value || 0) })}
                                min={0}
                            />
                        </label>
                        <label>
                            Bottom
                            <input
                                type="number"
                                value={hp.bottom ?? 0}
                                onChange={(e) => updateHP({ bottom: Number(e.target.value || 0) })}
                                min={0}
                            />
                        </label>
                        <label>
                            Left
                            <input
                                type="number"
                                value={hp.left ?? 0}
                                onChange={(e) => updateHP({ left: Number(e.target.value || 0) })}
                                min={0}
                            />
                        </label>
                    </div>
                )}
            </div>

            <div className="header-editor header-section">
                <h4 className="header-section-title">List Pages</h4>
                <div className="header-section-grid grid-2 header-section-grid--spaced">
                    {COMPONENT_TYPE_LIST_OPTIONS.map((option) => {
                        const count = Number(listCounts[option.type] || 0);
                        return (
                            <label key={option.type} className="header-list-toggle">
                                <span className="header-list-toggle-main">
                                    <input
                                        type="checkbox"
                                        checked={normalizedComponentListSettings[option.type] !== false}
                                        onChange={(e) => updateComponentListSetting(option.type, e.target.checked)}
                                    />
                                    <span>{option.listLabel}</span>
                                </span>
                                <span className="header-list-toggle-count">
                                    {option.showCount === false
                                        ? 'Profile page button'
                                        : `${count} ${count === 1 ? 'component' : 'components'}`}
                                </span>
                            </label>
                        );
                    })}
                </div>
                <div className="header-section-grid grid-3 header-section-grid--spaced">
                    <label className="header-list-toggle header-list-toggle--single">
                        <span className="header-list-toggle-main">
                            <input
                                type="checkbox"
                                checked={showFollowerCount === true}
                                onChange={(e) => onShowFollowerCountChange?.(e.target.checked)}
                            />
                            <span>Show follower count</span>
                        </span>
                    </label>
                    <label>
                        Placement
                        <select
                            value={normalizedComponentListSettings.placement}
                            onChange={(e) => updateComponentListSettings({ placement: e.target.value })}
                        >
                            {COMPONENT_LIST_PLACEMENTS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Alignment
                        <select
                            value={normalizedComponentListSettings.alignment}
                            onChange={(e) => updateComponentListSettings({ alignment: e.target.value })}
                        >
                            {COMPONENT_LIST_ALIGNMENTS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Width
                        <select
                            value={normalizedComponentListSettings.width}
                            onChange={(e) => updateComponentListSettings({ width: e.target.value })}
                        >
                            {COMPONENT_LIST_WIDTHS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>
                </div>
                <div className="header-section-grid grid-4 header-section-grid--spaced header-list-style-controls">
                    <label>
                        Button Color
                        <ColorInput
                            value={componentListButtonStyle.backgroundColor || '#ffffff'}
                            onChange={(e) => updateComponentListButtonStyle({ backgroundColor: e.target.value })}
                        />
                    </label>
                    <label>
                        Text Color
                        <ColorInput
                            value={componentListButtonStyle.textColor || '#183f43'}
                            onChange={(e) => updateComponentListButtonStyle({ textColor: e.target.value })}
                        />
                    </label>
                    <label>
                        Border Color
                        <ColorInput
                            value={componentListButtonStyle.borderColor || '#183f43'}
                            onChange={(e) => updateComponentListButtonStyle({ borderColor: e.target.value })}
                        />
                    </label>
                    <label>
                        Hover Color
                        <ColorInput
                            value={componentListButtonStyle.hoverBackgroundColor || '#183f43'}
                            onChange={(e) => updateComponentListButtonStyle({ hoverBackgroundColor: e.target.value })}
                        />
                    </label>
                    <label>
                        Hover Text
                        <ColorInput
                            value={componentListButtonStyle.hoverTextColor || '#ffffff'}
                            onChange={(e) => updateComponentListButtonStyle({ hoverTextColor: e.target.value })}
                        />
                    </label>
                    <label>
                        Border Width
                        <input
                            type="number"
                            min={0}
                            value={componentListButtonStyle.borderWidth ?? 2}
                            onChange={(e) => updateComponentListButtonStyle({ borderWidth: Number(e.target.value || 0) })}
                        />
                    </label>
                    <label>
                        Border Radius
                        <input
                            type="number"
                            min={0}
                            value={componentListButtonStyle.borderRadius ?? 8}
                            onChange={(e) => updateComponentListButtonStyle({ borderRadius: Number(e.target.value || 0) })}
                        />
                    </label>
                    <label>
                        Font Size
                        <input
                            type="number"
                            min={10}
                            value={componentListButtonStyle.fontSize ?? 14}
                            onChange={(e) => updateComponentListButtonStyle({ fontSize: Number(e.target.value || 14) })}
                        />
                    </label>
                    <label>
                        Font Weight
                        <select
                            value={componentListButtonStyle.fontWeight ?? 600}
                            onChange={(e) => updateComponentListButtonStyle({ fontWeight: Number(e.target.value || 600) })}
                        >
                            <option value={400}>Regular</option>
                            <option value={600}>Semi Bold</option>
                            <option value={700}>Bold</option>
                        </select>
                    </label>
                    <label>
                        Padding X
                        <input
                            type="number"
                            min={0}
                            value={componentListButtonStyle.paddingX ?? 12}
                            onChange={(e) => updateComponentListButtonStyle({ paddingX: Number(e.target.value || 0) })}
                        />
                    </label>
                    <label>
                        Padding Y
                        <input
                            type="number"
                            min={0}
                            value={componentListButtonStyle.paddingY ?? 6}
                            onChange={(e) => updateComponentListButtonStyle({ paddingY: Number(e.target.value || 0) })}
                        />
                    </label>
                    <label>
                        Gap
                        <input
                            type="number"
                            min={0}
                            value={componentListButtonStyle.gap ?? 10}
                            onChange={(e) => updateComponentListButtonStyle({ gap: Number(e.target.value || 0) })}
                        />
                    </label>
                </div>
            </div>

            <div className="header-editor-actions">
                <button type="button" className="muulti-button" onClick={onSave}>
                    Save Header
                </button>
            </div>
        </div>
    );
}
